import { useEffect, useState } from 'react'
import { useUserLanguage } from '@/customHooks'
import { capitalizeFirstLetter } from '@/helpers'
import { UnityStatusOption } from '@/graphql/generated'
import { UnityStatusOptionPill } from '../atoms'

export type PropertyForSimpleListParams = {
  id: string,
  name: string,
  street: string,
  city: string,
  state: string,
  status: UnityStatusOption
}

type SelectedPropertyParams = Omit<PropertyForSimpleListParams, 'street' | 'city' | 'state' | 'status'>

type PropertySimpleSelectionListProps = {
  selected: (property: SelectedPropertyParams) => void;
  properties: PropertyForSimpleListParams[] | undefined;
  disabledOn?: (property: PropertyForSimpleListParams) => boolean
}

const textsData = {
  en: {
    property: {
      name: 'Name',
      street: 'Street',
      city: 'City',
      state: 'State',
      status: 'Status',
    },
  },
  es: {
    property: {
      name: 'Nombre',
      street: 'Calle',
      city: 'Ciudad',
      state: 'Estado',
      status: 'Estado',
    },
  }
}

export const PropertySimpleSelectionList = ({ selected, properties, disabledOn }: PropertySimpleSelectionListProps) => {
  const userLanguage = useUserLanguage()
  const texts = userLanguage === 'es' ? textsData.es : textsData.en

  const [selectedProperty, setSelectedProperty] = useState<SelectedPropertyParams | null>(null)
  
  useEffect(() => {
    if (!properties || !selectedProperty) return

    selected({
      id: selectedProperty.id,
      name: selectedProperty.name,
    })
  }, [selectedProperty])

  return (
    <div className="py-6">
      <div className="h-[60vh] overflow-y-auto rounded-xl grid grid-cols-[64px_1fr_1fr_1fr_1fr_120px] content-start w-full max-w-full min-w-[300px] min-[500px]:min-w-[500px]">
        <div className="contents">
          <div className="font-bold text-left p-[15px] border-t border-b border-gray-500 bg-white sticky top-0 z-10"></div>
          <div className="font-bold text-left p-[15px] border-t border-b border-gray-500 bg-white sticky top-0 z-10">{texts.property.name}</div>
          <div className="font-bold text-left p-[15px] border-t border-b border-gray-500 bg-white sticky top-0 z-10">{texts.property.street}</div>
          <div className="font-bold text-left p-[15px] border-t border-b border-gray-500 bg-white sticky top-0 z-10">{texts.property.city}</div>
          <div className="font-bold text-left p-[15px] border-t border-b border-gray-500 bg-white sticky top-0 z-10">{texts.property.state}</div>
          <div className="font-bold text-left p-[15px] border-t border-b border-gray-500 bg-white sticky top-0 z-10">{texts.property.status}</div>
        </div>
        {properties?.map((property) => {
          return(
            <div key={property.id} className="contents group">
              <div className="p-[15px] text-sm flex items-center border-b border-gray-200 group-odd:bg-white group-even:bg-[--color-background-2] min-h-[50px]">
                <input
                  type="radio"
                  checked={selectedProperty?.id === property.id}
                  onChange={() => setSelectedProperty(property)}
                  className="cursor-pointer"
                  disabled={disabledOn?.(property) ?? false}
                />
              </div>
              <div className="p-[15px] text-sm flex items-center border-b border-gray-200 group-odd:bg-white group-even:bg-[--color-background-2] min-h-[50px]">
                {capitalizeFirstLetter(property.name)}
              </div>
              <div className="p-[15px] text-sm flex items-center border-b border-gray-200 group-odd:bg-white group-even:bg-[--color-background-2] min-h-[50px]">
                {capitalizeFirstLetter(property.street)}
              </div>
              <div className="p-[15px] text-sm flex items-center border-b border-gray-200 group-odd:bg-white group-even:bg-[--color-background-2] min-h-[50px]">
                {capitalizeFirstLetter(property.city)}
              </div>
              <div className="p-[15px] text-sm flex items-center border-b border-gray-200 group-odd:bg-white group-even:bg-[--color-background-2] min-h-[50px]">
                {capitalizeFirstLetter(property.state)}
              </div>
              <div className="p-[15px] text-sm flex items-center border-b border-gray-200 group-odd:bg-white group-even:bg-[--color-background-2] min-h-[50px]">
                <UnityStatusOptionPill option={property.status} />
              </div>
            </div>
          )
        }) ?? null
      }
      </div>
    </div>
  );
};
